import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { ResetOptions, usePaginatedApi } from '@front-app/hooks/usePaginatedApi';
import { InvitationCampaignInvitee, InviteeStatus, SortParameter } from '@front-app/services/GroupInvitationCampaignApi/model';
import { SortDirection } from '@front-app/services/SharedModels';
import { sum } from '@front-app/util/math';
import { computed, ref, Ref } from '@vue/composition-api';
import axios from 'axios';
import { InvitationCampaignInviteesStore } from '../singletons/InvitationCampaignInvitees';
import { InvitationCampaignInviteeStore } from './invitationCampaignInvitees';

const DISPLAY_PAGE_LENGTH = 20;

/**
 * Encapsulates a searchable prefetched paginated campaign
 * invitee list
 */
export interface InvitationCampaignInviteeSearchStore {
	// Actions
	reset: (options: ResetOptions) => void;
	loadMore: () => void;
	// State
	inviteesToRender: Ref<InvitationCampaignInvitee[]>;
	hasNextPage: Ref<boolean>;
	isFetchingInvitees: Ref<boolean>;
	isLoadingCurrentPage: Ref<boolean>;
}

export interface CampaignInviteeSearchArgs {
	groupId: string;
	campaignId: string;
	search: string;
	orderBy: SortParameter;
	sortDirection: SortDirection;
	status: InviteeStatus[];
}

export const getInvitationCampaignInviteesSearchStore = (
	inviteeStore: InvitationCampaignInviteeStore,
	state: Ref<CampaignInviteeSearchArgs>,
): InvitationCampaignInviteeSearchStore => {
	const {
		track,
		isLoading,
	} = useAsyncStatus(undefined, { shouldHandleError: (e) => !axios.isCancel(e) });
	const totalCountForSelectedStatuses = computed(() => state.value.status.map(inviteeStore.getCountForStatus).reduce(sum));
	const currentPage = ref(1);
	const hasNextPage = ref(true);
	const fetchInviteesForPage = async (apiPageNum: number) => {
		const results = await InvitationCampaignInviteesStore.fetchInviteesPage(state.value.groupId, state.value.campaignId, apiPageNum, {
			search: state.value.search,
			orderBy: state.value.orderBy,
			sortDirection: state.value.sortDirection,
			status: state.value.status,
		});
		return results;
	};
	const {
		getPageAt,
		fetchPagesAround,
		resetToFirstPage,
	} = usePaginatedApi(fetchInviteesForPage, {
		renderedPageLength: DISPLAY_PAGE_LENGTH,
		fetchedPageLength: 100,
		numItems: totalCountForSelectedStatuses,
	});
	const reset = (args: ResetOptions) => {
		hasNextPage.value = true;
		currentPage.value = 1;
		track(resetToFirstPage(args));
	};
	const loadMore = () => {
		currentPage.value += 1;
		track(fetchPagesAround(currentPage.value));

		const totalNumberOfPages = computed(() => Math.ceil(totalCountForSelectedStatuses.value / DISPLAY_PAGE_LENGTH));
		if (currentPage.value >= totalNumberOfPages.value) {
			hasNextPage.value = false;
		}
	};
	const inviteesToRender = computed(() => {
		const searchVal = state.value.search.toLocaleLowerCase();
		return new Array(currentPage.value)
			.fill(undefined)
			.flatMap((_, index) => getPageAt(index + 1))
			.map(InvitationCampaignInviteesStore.inviteeById)
			.filter(({ email }) => email.toLocaleLowerCase().includes(searchVal));
	});
	const isFetchingInvitees = computed(() => isLoading.value && inviteesToRender.value.length === 0);
	return {
		// Actions
		reset,
		loadMore,
		// State
		inviteesToRender,
		hasNextPage,
		isFetchingInvitees,
		isLoadingCurrentPage: isLoading,
	};
};
